// src/pages/Trend/components/analysis/sustainability/SustainabilityStatusChart.js
import React, { memo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { ErrorMessage } from '../../../../../components/common/UI';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import './SustainabilityStatusChart.css';

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

// Narrative component following design guide
const SustainabilityNarrative = ({ primaryData, secondaryData, hasSecondary, translate }) => {
  const primaryScore = primaryData?.sustainability_score || 0;
  const secondaryScore = secondaryData?.sustainability_score || 0;

  const getScoreLevel = (score) => {
    if (score >= 70) return { level: translate('trendPage.sustainability.levels.high'), color: BBC_STYLE.POSITIVE_COLOR };
    if (score >= 40) return { level: translate('trendPage.sustainability.levels.medium'), color: BBC_STYLE.SECONDARY_COLOR };
    return { level: translate('trendPage.sustainability.levels.low'), color: BBC_STYLE.NEGATIVE_COLOR };
  };

  const primaryCategories = Object.keys(primaryData?.category_scores || {});
  const primaryHighest = primaryCategories.length > 0
    ? primaryCategories.reduce((max, cat) =>
        (primaryData.category_scores[cat] > primaryData.category_scores[max] ? cat : max),
        primaryCategories[0]
      )
    : '';

  if (!hasSecondary) {
    const { level, color } = getScoreLevel(primaryScore);
    return (
      <div className="sustainability-narrative">
        <h2 className="narrative-title">
          <span
            className="highlight-level"
            style={{ color, borderBottom: `2px solid ${color}` }}
          >
            {level}
          </span>
          {' '}{translate('trendPage.sustainability.narrative.participationLevel')}
        </h2>
        <div className="narrative-subtitle">
          <span className="stat-pill" style={{ background: `${BBC_STYLE.PRIMARY_COLOR}15` }}>
            {translate('trendPage.sustainability.overallScore')}: {primaryScore}{translate('trendPage.sustainability.points')}
          </span>
          <span className="stat-divider">•</span>
          <span>{translate('trendPage.sustainability.topCategory')}: <strong>{primaryHighest}</strong></span>
        </div>
      </div>
    );
  }

  // Comparison mode
  const diff = Math.abs(primaryScore - secondaryScore);
  const similar = diff <= 5;
  const higherSet = primaryScore > secondaryScore ? translate('trendPage.filters.set1') : translate('trendPage.filters.set2');
  const higherColor = primaryScore > secondaryScore ? BBC_STYLE.PRIMARY_COLOR : BBC_STYLE.SECONDARY_COLOR;

  const secondaryCategories = Object.keys(secondaryData?.category_scores || {});
  const secondaryHighest = secondaryCategories.length > 0
    ? secondaryCategories.reduce((max, cat) =>
        (secondaryData.category_scores[cat] > secondaryData.category_scores[max] ? cat : max),
        secondaryCategories[0]
      )
    : '';

  return (
    <div className="sustainability-narrative">
      <h2 className="narrative-title">
        {similar ? (
          <>
            {translate('trendPage.sustainability.narrative.bothFilters')}{' '}
            <span className="highlight-level" style={{ color: BBC_STYLE.HIGHLIGHT_COLOR, borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}` }}>
              {translate('trendPage.sustainability.narrative.similar')}
            </span>
            {' '}{translate('trendPage.sustainability.narrative.sustainabilityLevel')}
          </>
        ) : (
          <>
            <span className="highlight-level" style={{ color: higherColor, borderBottom: `2px solid ${higherColor}` }}>
              {higherSet}
            </span>
            {' '}{translate('trendPage.sustainability.narrative.higherBy', { diff })}
          </>
        )}
      </h2>
      <div className="narrative-comparison">
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
          <span className="comparison-value" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>
            {primaryScore}{translate('trendPage.sustainability.points')}
          </span>
          <span className="comparison-category">({primaryHighest})</span>
        </div>
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
          <span className="comparison-value" style={{ color: BBC_STYLE.SECONDARY_COLOR }}>
            {secondaryScore}{translate('trendPage.sustainability.points')}
          </span>
          <span className="comparison-category">({secondaryHighest})</span>
        </div>
      </div>
    </div>
  );
};

const RadarChartComponent = memo(({
  primaryData,
  secondaryData,
  hasSecondaryData,
  primaryMetrics,
  secondaryMetrics,
  primaryTotalProducts,
  secondaryTotalProducts,
  translate
}) => {
  if (!primaryData || primaryData.length === 0) return null;

  const filter1Label = translate('trendPage.filters.set1');
  const filter2Label = translate('trendPage.filters.set2');

  const combinedData = primaryData.map((item) => {
    const secondaryItem = secondaryData?.find(s => s.subject === item.subject);
    return {
      subject: item.subject,
      [filter1Label]: item.value,
      ...(hasSecondaryData && secondaryItem ? { [filter2Label]: secondaryItem.value } : {})
    };
  });

  const shortenName = (name) => {
    const nameMap = {
      'Environmental Protection Campaign Participation': 'Eco-Campaign',
      'Other environmental protection activities participation': 'Other eco activities',
    };
    return nameMap[name] || name;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // Get metrics for the hovered category
    const primaryCategoryMetrics = primaryMetrics?.[label];
    const secondaryCategoryMetrics = secondaryMetrics?.[label];

    const getActivities = (metrics) => {
      if (!metrics?.labels || !metrics?.values) return [];
      return metrics.labels.map((name, idx) => ({
        name: shortenName(name),
        count: metrics.values[idx],
        active: metrics.values[idx] > 0
      }));
    };

    const primaryActivities = getActivities(primaryCategoryMetrics);
    const secondaryActivities = getActivities(secondaryCategoryMetrics);

    return (
      <div className="radar-tooltip">
        {/* Header with category name */}
        <div className="tooltip-header">
          <span className="tooltip-category">{shortenName(label)}</span>
        </div>

        {/* Score comparison */}
        <div className="tooltip-scores">
          {payload.map((entry, index) => (
            <div key={index} className="tooltip-score-item">
              <span className="tooltip-dot" style={{ background: entry.color }} />
              <span className="tooltip-score-label">{entry.name}</span>
              <span className="tooltip-score-value" style={{ color: entry.color }}>
                {entry.value}%
              </span>
            </div>
          ))}
        </div>

        {/* Activity breakdown */}
        {primaryActivities.length > 0 && (
          <div className="tooltip-activities">
            {!hasSecondaryData ? (
              // Single mode
              <div className="tooltip-activity-list">
                {primaryActivities.map((activity, idx) => (
                  <div key={idx} className={`tooltip-activity-item ${activity.active ? 'active' : 'inactive'}`}>
                    <span className="tooltip-activity-name">{activity.name}</span>
                    <span
                      className="tooltip-activity-count"
                      style={activity.active ? { color: BBC_STYLE.PRIMARY_COLOR } : {}}
                    >
                      {activity.active ? `${activity.count}/${primaryTotalProducts}` : '-'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              // Comparison mode
              <div className="tooltip-activity-comparison">
                {primaryActivities.map((activity, idx) => {
                  const secondaryActivity = secondaryActivities[idx];
                  return (
                    <div key={idx} className="tooltip-activity-row">
                      <span className="tooltip-activity-name">{activity.name}</span>
                      <div className="tooltip-activity-values">
                        <span
                          className="tooltip-activity-count"
                          style={{ color: activity.active ? BBC_STYLE.PRIMARY_COLOR : '#bbb' }}
                        >
                          {activity.active ? activity.count : '-'}
                        </span>
                        <span className="tooltip-activity-divider">/</span>
                        <span
                          className="tooltip-activity-count"
                          style={{ color: secondaryActivity?.active ? BBC_STYLE.SECONDARY_COLOR : '#bbb' }}
                        >
                          {secondaryActivity?.active ? secondaryActivity.count : '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <RadarChart data={combinedData}>
        <PolarGrid stroke="#e5e7eb" />
        <PolarAngleAxis
          dataKey="subject"
          tick={{ fill: '#333', fontSize: 10, fontWeight: '400' }}
          stroke="#e5e7eb"
        />
        <Radar
          name={filter1Label}
          dataKey={filter1Label}
          stroke={BBC_STYLE.PRIMARY_COLOR}
          fill={BBC_STYLE.PRIMARY_COLOR}
          fillOpacity={0.25}
          strokeWidth={2}
        />
        {hasSecondaryData && (
          <Radar
            name={filter2Label}
            dataKey={filter2Label}
            stroke={BBC_STYLE.SECONDARY_COLOR}
            fill={BBC_STYLE.SECONDARY_COLOR}
            fillOpacity={0.25}
            strokeWidth={2}
          />
        )}
        <Tooltip content={<CustomTooltip />} />
      </RadarChart>
    </ResponsiveContainer>
  );
});

const SustainabilityStatusChart = memo(({ data }) => {
  const { translate } = useLanguage();

  if (!data?.primary?.sustainability_metrics) {
    return <ErrorMessage message={translate('trendPage.sustainability.errors.noStatusData')} />;
  }

  const { primary, secondary, metadata } = data;
  const hasSecondaryData = secondary && Object.keys(secondary).length > 0;

  const primaryTotalProducts = primary.total_products || metadata?.primary_group_count || 0;
  const secondaryTotalProducts = secondary?.total_products || metadata?.secondary_group_count || 0;

  const getRadarData = (categoryScores) => {
    if (!categoryScores) return [];
    return Object.entries(categoryScores).map(([key, value]) => ({
      subject: key,
      value: value || 0,
      fullMark: 100
    }));
  };

  const primaryRadarData = getRadarData(primary.category_scores);
  const secondaryRadarData = hasSecondaryData ? getRadarData(secondary.category_scores) : null;

  return (
    <div className="sustainability-wrapper">
      {/* Narrative Section */}
      <SustainabilityNarrative
        primaryData={primary}
        secondaryData={secondary}
        hasSecondary={hasSecondaryData}
        translate={translate}
      />

      {/* Main Content: Scores (comparison only) + Radar */}
      <div className={`sustainability-main-content ${hasSecondaryData ? 'has-comparison' : ''}`}>
        {/* Score Cards - only show in comparison mode */}
        {hasSecondaryData && (
          <div className="sustainability-scores">
            <div className="score-card">
              <div className="score-card-header">
                <span className="score-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
                <span className="score-label">{renderFilterLabel(translate('trendPage.filters.set1'))}</span>
              </div>
              <div className="score-value" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>
                {primary.sustainability_score}%
              </div>
            </div>
            <div className="score-card">
              <div className="score-card-header">
                <span className="score-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
                <span className="score-label">{renderFilterLabel(translate('trendPage.filters.set2'))}</span>
              </div>
              <div className="score-value" style={{ color: BBC_STYLE.SECONDARY_COLOR }}>
                {secondary.sustainability_score}%
              </div>
            </div>
          </div>
        )}

        {/* Radar Chart with tooltip showing activity breakdown */}
        <div className="sustainability-radar">
          <RadarChartComponent
            primaryData={primaryRadarData}
            secondaryData={secondaryRadarData}
            hasSecondaryData={hasSecondaryData}
            primaryMetrics={primary.sustainability_metrics}
            secondaryMetrics={secondary?.sustainability_metrics}
            primaryTotalProducts={primaryTotalProducts}
            secondaryTotalProducts={secondaryTotalProducts}
            translate={translate}
          />
        </div>
      </div>
    </div>
  );
});

export default SustainabilityStatusChart;
